import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { portal as portalApi } from '../services/api';
import {
  ACTION_STATUSES, statusLabel, priorityLabel,
  formatShortDate, relativeDays, isOverdue, segmentLabel,
} from '../utils/portal';

/**
 * One action item: status, assignee, due date, optional map link, and an
 * expandable progress thread.
 *
 * The assignee can move the status; chairs and admins can do anything.
 * Any volunteer can add a progress note.
 */
export default function PortalActionItem({
  action, onChanged, onEdit, showMeeting = true, defaultExpanded = false,
}) {
  const { user, isAdmin, isChair } = useAuth();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [updates, setUpdates] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const canManage = isAdmin || isChair;
  const isAssignee = action.assignee_id === user?.id;
  const canSetStatus = canManage || isAssignee;
  const overdue = isOverdue(action);
  const segLabel = segmentLabel(action);

  const loadUpdates = async () => {
    try {
      const full = await portalApi.getAction(action.id);
      setUpdates(full.updates || []);
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleExpanded = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && updates === null) loadUpdates();
  };

  const changeStatus = async (status) => {
    setBusy(true);
    setError(null);
    try {
      await portalApi.updateAction(action.id, { status });
      if (onChanged) await onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const created = await portalApi.addActionUpdate(action.id, note.trim());
      setUpdates([...(updates || []), created]);
      setNote('');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const deleteNote = async (id) => {
    try {
      await portalApi.deleteActionUpdate(id);
      setUpdates((updates || []).filter((u) => u.id !== id));
    } catch (err) {
      setError(err.message);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${action.title}"? This can't be undone.`)) return;
    setBusy(true);
    try {
      await portalApi.deleteAction(action.id);
      if (onChanged) await onChanged();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div className={`portal-action status-${action.status} priority-${action.priority} ${overdue ? 'is-overdue' : ''}`}>
      <div className="portal-action-main">
        <button
          className="portal-action-check"
          title={action.status === 'done' ? 'Reopen' : 'Mark done'}
          disabled={!canSetStatus || busy}
          onClick={() => changeStatus(action.status === 'done' ? 'open' : 'done')}
        >
          {action.status === 'done' ? '☑' : '☐'}
        </button>

        <div className="portal-action-body">
          <button className="portal-action-title" onClick={toggleExpanded}>
            {action.title}
          </button>

          <div className="portal-action-meta">
            <span className={`portal-badge badge-${action.status}`}>{statusLabel(action.status)}</span>

            {action.priority !== 'normal' && (
              <span className={`portal-badge badge-priority-${action.priority}`}>
                {priorityLabel(action.priority)} priority
              </span>
            )}

            <span className="portal-action-assignee">
              {action.assignee_name
                ? <>👤 {action.assignee_name}{isAssignee ? ' (you)' : ''}</>
                : <em className="unassigned">Unassigned</em>}
            </span>

            {action.due_date && (
              <span className={`portal-action-due ${overdue ? 'overdue' : ''}`}>
                📅 {formatShortDate(action.due_date)} · {relativeDays(action.due_date)}
              </span>
            )}

            {segLabel && (
              <Link className="portal-action-segment" to={`/?segment=${action.segment_id}`} title="Show on the map">
                🗺 {segLabel}
              </Link>
            )}

            {showMeeting && action.meeting_id && action.meeting_title && (
              <Link className="portal-action-meeting" to={`/volunteer/meetings/${action.meeting_id}`}>
                📋 {action.meeting_title}
              </Link>
            )}
          </div>
        </div>

        <div className="portal-action-controls">
          {canSetStatus && (
            <select
              value={action.status}
              disabled={busy}
              onChange={(e) => changeStatus(e.target.value)}
              className="portal-status-select"
            >
              {ACTION_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          )}
          {canManage && onEdit && (
            <button className="btn-sm btn-secondary" onClick={() => onEdit(action)}>Edit</button>
          )}
          {canManage && (
            <button className="btn-sm btn-danger" onClick={remove} disabled={busy}>Delete</button>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {expanded && (
        <div className="portal-action-detail">
          {action.details && <p className="portal-action-details">{action.details}</p>}

          <div className="portal-action-provenance">
            Added by {action.created_by_name || 'someone'}
            {action.meeting_title && !showMeeting && <> at {action.meeting_title}</>}
            {action.completed_at && <> · completed {formatShortDate(action.completed_at)}</>}
          </div>

          <div className="portal-action-thread">
            <h4>Progress notes</h4>
            {updates === null && <p className="portal-muted">Loading…</p>}
            {updates !== null && updates.length === 0 && (
              <p className="portal-muted">No notes yet.</p>
            )}
            {(updates || []).map((u) => (
              <div className="portal-note" key={u.id}>
                <div className="portal-note-head">
                  <strong>{u.user_name || 'Unknown'}</strong>
                  <span>{new Date(u.created_at).toLocaleString()}</span>
                  {(u.user_id === user?.id || isAdmin) && (
                    <button className="portal-note-delete" onClick={() => deleteNote(u.id)} title="Delete note">✕</button>
                  )}
                </div>
                <p>{u.body}</p>
              </div>
            ))}

            <form className="portal-note-form" onSubmit={addNote}>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add a progress note…"
              />
              <button type="submit" className="btn-sm btn-primary" disabled={busy || !note.trim()}>Post</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
